<template>
  <!-- Figma "Service & Repair - Equipment Diagnosis" (node 5376:897): legend, Outdoor/Indoor columns as lists,
       three colour radios per row. Ids/names/@change kept — validation scrolls to 'outdoor-<i>' / 'indoor-<i>'. -->
  <main class="evalFigma w-full outdoorEquipPg relative">
    <EvalShell title="Equipment Diagnosis" :step="2" :job-id="$route.params.id" :unit-id="$route.query.unit_id">
      <!-- Figma C2 (Incomplete): shown after Next while components are still unassessed -->
      <template #alert>
        <div v-if="showIncomplete && unassessedCount > 0"
          class="flex items-center gap-[12px] mb-[20px] px-[18px] py-[13px] rounded-[10px] border-1 border-[#F3D79A] bg-[#FEF6E6] font-roboto">
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" class="flex-shrink-0">
            <path fill="#F5B301" d="M12 2 1 21h22z" />
            <path d="M12 9v5M12 17h.01" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" />
          </svg>
          <p class="m-0 text-[14px] leading-[20px] font-medium text-[#9A6704]">
            {{ unassessedCount }} {{ unassessedCount == 1 ? 'component has' : 'components have' }} not been assessed
            yet. Mark every component before continuing.
          </p>
        </div>
      </template>

      <!-- below laptop the two lists stack, chips jump between them -->
      <div v-if="equipmentChecksList.indoor && equipmentChecksList.outdoor"
        ref="tabBar" class="outdoorEquipPgHead laptop:hidden sticky z-[5] mb-3 -mx-[10px] !px-[10px] tablet:-mx-[15px] tablet:!px-[15px] !py-[8px] bg-[#fff]"
        :style="{ top: headerHeight + 'px' }">
        <div class="flex gap-2">
          <button v-for="tab in ['outdoor', 'indoor']" :key="tab" type="button"
            class="px-3 py-1 rounded-full text-[14px] font-roboto border-1"
            :class="selectedTab == tab ? 'bg-[#7695FF] border-[#7695FF] text-white' : 'bg-white border-[#CCCCCC] text-[#333333]'"
            @click="tabClick(tab)">
            {{ tab == 'outdoor' ? 'Outdoor Unit' : 'Indoor Unit' }}
          </button>
        </div>
      </div>

      <!-- Figma C1 (5588:1306): legend left, status counters right -->
      <div class="flex flex-wrap items-start justify-between gap-[15px] mb-[20px]">
        <ul class="list-none m-0 p-0 flex flex-col gap-[6px] text-[13px] leading-[19px] font-roboto text-[#5B616B]">
          <li v-for="item in radios" :key="item.value" class="flex items-center gap-[8px]">
            <span class="w-[16px] h-[16px] rounded-full border-[1.5px] bg-white flex-shrink-0"
              :style="{ borderColor: item.color }" />
            {{ item.legend }}
          </li>
        </ul>
        <div class="flex flex-wrap items-center gap-[10px]">
          <span v-for="item in radios" :key="item.value"
            class="flex items-center gap-[8px] px-[8px] py-[3px] laptop:h-[41px] laptop:pl-[14px] laptop:pr-[16px] rounded-[5px] laptop:rounded-[8px] font-roboto"
            :style="{ backgroundColor: item.chipBg, color: item.chipText }">
            <span class="text-[14px] laptop:text-[16px] leading-[23px] font-bold">{{ statusCount(item.value) }}</span>
            <span class="text-[12px] laptop:text-[13px] leading-[19px]">{{ item.label }}</span>
          </span>
          <span v-if="unassessedCount > 0"
            class="flex items-center gap-[8px] h-[41px] pl-[14px] pr-[16px] rounded-[8px] bg-[#F1F3F7] text-[#5B616B] font-roboto">
            <span class="text-[16px] leading-[23px] font-bold">{{ unassessedCount }}</span>
            <span class="text-[13px] leading-[19px]">Not assessed</span>
          </span>
        </div>
      </div>

      <!-- search + Show all / Issues only (client-side filter; rows stay in the DOM so validation ids keep their index) -->
      <div class="flex flex-wrap items-center justify-between gap-[10px] mb-[20px]">
        <label
          class="flex items-center gap-[10px] w-full tablet:w-[340px] h-[44px] m-0 px-[16px] rounded-[8px] border-1 border-[#DFE3EA] bg-white">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="#A8AEB8" stroke-width="2" />
            <path d="M20 20l-3.5-3.5" stroke="#A8AEB8" stroke-width="2" stroke-linecap="round" />
          </svg>
          <input v-model="search" type="text" placeholder="Search a component…"
            class="flex-1 min-w-0 border-0 outline-none bg-transparent text-[14px] leading-[20px] font-roboto text-[#2E3138] placeholder:text-[#A8AEB8]">
        </label>
        <div class="flex items-center gap-[10px] mobile:w-full tablet:w-auto">
          <button v-for="f in [{ key: 'all', label: 'Show all' }, { key: 'issues', label: 'Issues only' }]" :key="f.key"
            type="button"
            class="mobile:flex-1 tablet:flex-none h-[41px] px-[18px] rounded-[8px] border-1 text-[13px] font-medium font-roboto"
            :class="filter == f.key ? 'bg-[#6C79E0] border-[#6C79E0] text-white' : 'bg-white border-[#E3E6EC] text-[#5B616B]'"
            @click="filter = f.key">
            {{ f.label }}
          </button>
        </div>
      </div>

      <!-- 2 columns from 1280: at 1024 the card next to Details is too narrow and the status circles were clipped (QA D-38) -->
      <div class="grid gap-[20px]" :class="equipmentSections.length > 1 ? 'desktop:grid-cols-2' : ''">
        <div v-for="section in equipmentSections" :id="section.anchor" :key="section.key"
          class="section min-w-0 flex flex-col gap-[2px] laptop:gap-[7px]">
          <!-- Figma: header 47px #A3AAD2 r8, Roboto Medium 16 white; rows 44px r8, 1px border, Roboto 14 #2E3138 -->
          <h5
            class="m-0 mb-[4px] min-h-[47px] flex items-center px-[20px] py-[12px] rounded-[8px] text-[16px] leading-[23px] font-roboto font-medium text-white bg-[#A3AAD2]">
            {{ section.title }}
          </h5>
          <div v-for="(item, index) in section.options" v-show="isVisible(item)" :id="section.key + '-' + index"
            :key="index"
            class="flex items-center justify-between gap-[12px] min-h-[44px] px-[18px] py-[11px] rounded-[8px] border-1"
            :class="rowClass(item)">
            <span class="text-[14px] leading-[20px] font-roboto text-[#2E3138]">{{ item.option }}</span>
            <div class="flex items-center gap-[6px] flex-shrink-0">
              <template v-for="radio in radios" :key="radio.value">
                <input :id="item.diagnostic_option_id + '-' + radio.name" :checked="item.value == radio.value"
                  type="radio" class="sr-only" :value="radio.value" :name="item.diagnostic_option_id"
                  @change="onChange(item, radio.value)">
                <!-- Figma: 18px, 1.5px ring on white; selected = solid with a white tick -->
                <label :for="item.diagnostic_option_id + '-' + radio.name" :title="radio.label"
                  class="w-[18px] h-[18px] m-0 rounded-full border-[1.5px] cursor-pointer transition flex items-center justify-center"
                  :style="{ borderColor: radio.color, backgroundColor: item.value == radio.value ? radio.color : '#FFFFFF' }">
                  <svg v-if="item.value == radio.value" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 64 64">
                    <path d="M0 0h64v64H0z" fill="none" />
                    <path fill="#FFFFFF" d="M56 2L18.8 42.909L8 34.729H2L18.8 62L62 2z" />
                  </svg>
                </label>
              </template>
            </div>
          </div>
          <p v-if="section.options.length && !section.options.some(isVisible)"
            class="m-0 py-4 text-center text-[13px] font-roboto text-[#9CA3AF]">
            No components match.
          </p>
        </div>
      </div>

      <!-- <div > -->
      <template #actions>
        <div
          class="absolute -bottom-[30px] left-0 right-0 laptop:relative laptop:bottom-0 flex flex-wrap justify-center items-center gap-[20px] laptop:mt-[30px] mobile:mb-[10px] laptop:mb-0">
          <EvalButton variant="back" @click="backButtonClicked()" />
          <EvalButton @click="nextButtonClicked()" />
        </div>
      </template>
      <!-- </div> -->
    </EvalShell>
  </main>
</template>

<script>
import EvalShell from "@/components/nest_components/EvalShell.vue";
import EvalButton from "@/components/nest_components/EvalButton.vue";
import { appHeaderBottom } from "@/helpers/appHeader";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification";
const toast = useToast();

export default {
  components: {
    EvalShell,
    EvalButton,
  },

  computed: {
    // Outdoor + Indoor side by side when both exist; otherwise one list titled after its unit (old 'outdoorSec')
    equipmentSections() {
      if (this.equipmentChecksList.indoor && this.equipmentChecksList.outdoor) {
        return [
          { key: 'outdoor', anchor: 'outdoor', title: 'Outdoor Unit', options: this.outdoorOptions },
          { key: 'indoor', anchor: 'indoor', title: 'Indoor Unit', options: this.indoorOptions },
        ];
      }
      if (this.equipmentChecksList.indoor || this.equipmentChecksList.outdoor) {
        return [{ key: 'outdoor', anchor: 'outdoorSec', title: this.getUnitName(this.outdoorOptions) + ' Unit', options: this.outdoorOptions }];
      }
      return [];
    },
    ...mapGetters([
      "equipmentChecksList",
      "apiCallback",
      "storeChecksResponse",
      "getDiagnosisOptions",
      "bookingResponse",
    ]),
    unassessedCount() {
      return this.allOptions.filter(item => ![1, 2, 3].includes(Number(item.value))).length;
    },
    allOptions() {
      return this.equipmentSections.flatMap(section => section.options);
    },
    hasFixedClassOutdoor() {
      return this.minHeightOutdoor;
    },
    hasFixedClassIndoor() {
      return this.minHeightIndoor;
    },
  },

  data() {
    return {
      // Figma C1 colours (radio / counter chip bg / counter text)
      radios: [
        { value: 1, name: 'green', color: '#22C55E', chipBg: '#E7F7F0', chipText: '#1FA971', label: 'Operating', legend: "Operating as per manufacturer's specification" },
        { value: 2, name: 'yellow', color: '#F5B301', chipBg: '#FEF6E6', chipText: '#C98305', label: 'Needs attention', legend: "Not operating as per manufacturer's specification" },
        { value: 3, name: 'red', color: '#EF4444', chipBg: '#FDECEC', chipText: '#C43333', label: 'Critical', legend: 'Non-Operational / Critical' },
      ],
      search: '',
      // 'all' | 'issues'
      filter: 'all',
      // set by Next; shows the "not assessed yet" banner
      showIncomplete: false,
      selectedTab: "outdoor",
      jobId: this.$route.params.id,
      unit_id: this.$route.query.unit_id,
      // Indoor
      indoorOptions: [],
      indoorExistingOptions: [],
      // Outdoor
      outdoorOptions: [],
      outdoorExistingOptions: [],
      // the fixed app header's height: the chip bar sticks right under it
      headerHeight: 50,
      minHeightOutdoor: false,
      minHeightIndoor: false,
      pageOffset: 0,
      offsetTop: 0
    };
  },

  watch: {
    equipmentChecksList: function () {
      this.indoorOptions =
        (this.equipmentChecksList.indoor &&
          this.equipmentChecksList.indoor.body) ??
        [];
      this.outdoorOptions =
        (this.equipmentChecksList.outdoor &&
          this.equipmentChecksList.outdoor.body) ??
        [];

      this.getDiagnosisOption({
        booking_id: this.jobId,
        diagnosis_type: "equipment_checks",
      });

    },

    getDiagnosisOptions: function () {
      this.handleDiagnosisResponse();
    },

    storeChecksResponse: function () {
      if (this.storeChecksResponse && this.storeChecksResponse.diagnosis_type == "diagnosis_items") {
        this.$router.push({
          path: `/summary/${this.$route.params.id}`,
          query: {
            unit_id: this.unit_id,
            is_from_mobile: this.$route.query.is_from_mobile,
          },
        });
      } else {
        this.storeChecks({
          booking_id: this.jobId,
          diagnosis_type: "diagnosis_items",
          diagnosis_data: JSON.stringify({ diagnosis_items: [] }),
        });
      }
    },
    bookingResponse: function () {
      this.handleBookingResponse();
    },
  },

  mounted() {
    this.getBookingDetails({
      booking_id: this.jobId,
    });
    window.addEventListener("scroll", this.updateOffset);
    this.measureHeader();
  },

  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },

  methods: {
    statusCount(value) {
      return this.allOptions.filter(item => item.value == value).length;
    },

    // search text + "Issues only" (needs attention / critical / not assessed)
    isVisible(item) {
      const term = this.search.trim().toLowerCase();
      if (term && !(item.option || '').toLowerCase().includes(term)) return false;
      if (this.filter === 'issues') return item.value == 2 || item.value == 3 || !!item.highlight || ![1, 2, 3].includes(Number(item.value));
      return true;
    },

    // Figma row tints: needs attention yellow, critical red; validation highlight uses the critical tint
    rowClass(item) {
      if (item.value == 3 || item.highlight) return 'bg-[#FEF7F7] border-[#F5C9C9]';
      if (item.value == 2) return 'bg-[#FEFAF0] border-[#F6E3B8]';
      return 'bg-white border-[#EDEFF3]';
    },

    updateOffset() {
      // this.pageOffset =
      //   window.pageYOffset || document.documentElement.scrollTop;
      // console.log(this.pageOffset);
      // if(this.pageOffset <= 100){
      //   this.tabClick('outdoor')
      // }
    },


    ...mapActions([
      "getEquipmentChecks",
      "storeChecks",
      "getDiagnosisOption",
      "getBookingDetails",
    ]),

    tabClick(tab) {
      this.selectedTab = tab;
      if (tab === "outdoor") {
        this.minHeightOutdoor = true;
        this.minHeightIndoor = false;
      } else if (tab === "indoor") {
        this.minHeightOutdoor = false;
        this.minHeightIndoor = true;
      }

      const element = document.getElementById(tab);
      if (element) {
        const rect = element.getBoundingClientRect();
        // land the section right under the header + the sticky chip bar
        const barHeight = this.$refs.tabBar?.offsetHeight || 0;
        window.scrollTo({
          top: rect.top + window.pageYOffset - this.headerHeight - barHeight - 8,
          left: 0,
          behavior: "smooth",
        });
      }
    },
    handleBookingResponse() {
      if (this.bookingResponse !== null) {
        this.getEquipmentChecks({
          booking_id: this.jobId,
          unit_id: this.unit_id,
        });
        window.addEventListener("scroll", this.handleScroll);
      }
    },
    onChange(data, value) {
      data.highlight = false;
      data.value = value;
    },

    handleDiagnosisResponse() {
      if (this.getDiagnosisOptions[0]?.diagnosis_type == "equipment_checks") {
        this.setIndoorOptions();
        this.setOutdoorOptions();
      }
    },

    setIndoorOptions() {
      let diagnosis_data = JSON.parse(
        this.getDiagnosisOptions?.[0]?.diagnosis_data || 'null' /* no saved diagnosis yet → null, not a crash */
      );
      this.indoorExistingOptions = diagnosis_data?.indoor || [];
      let self = this;

      this.indoorOptions = this.indoorOptions.map(function (item) {
        self.indoorExistingOptions.forEach(function (obj) {
          if (
            parseInt(obj.diagnostic_option_id) ===
            parseInt(item.diagnostic_option_id)
          ) {
            item.value = obj.value;
          }
        });
        return item;
      });
    },

    setOutdoorOptions() {
      let diagnosis_data = JSON.parse(
        this.getDiagnosisOptions?.[0]?.diagnosis_data || 'null' /* no saved diagnosis yet → null, not a crash */
      );
      this.outdoorExistingOptions = diagnosis_data?.outdoor || [];
      let self = this;

      this.outdoorOptions = this.outdoorOptions.map(function (item) {
        self.outdoorExistingOptions.forEach(function (obj) {
          if (
            parseInt(obj.diagnostic_option_id) ===
            parseInt(item.diagnostic_option_id)
          ) {
            item.value = obj.value;
          }
        });
        return item;
      });
    },

    getUnitName(data) {
      let res = ''
      if (data && data.length > 0) {
        res = data[0].type_formatted
      }
      return res
    },
    nextButtonClicked() {
      // let indoor = this.checkOudoorValidation();
      // let outdoor = this.checkIndoorValidation();

      // show every row so validation can scroll to the first unanswered one
      this.search = '';
      this.filter = 'all';
      this.showIncomplete = true;
      let indoorOutdoor = this.checkIndoorOudoorValidation();

      if (this.bookingResponse.system_type == "Split" && !indoorOutdoor) {
        toast.error("Select all item.");
      } else if (
        this.bookingResponse.system_type == "Package" &&
        !indoorOutdoor
      ) {
        toast.error("Select all item.");
      } else {
        this.storeChecks({
          booking_id: this.jobId,
          diagnosis_type: "equipment_checks",
          diagnosis_data: JSON.stringify({
            indoor: this.indoorOptions,
            outdoor: this.outdoorOptions,
          }),
        });
      }
    },

    checkIndoorValidation() {
      let res = true;
      for (var key in this.indoorOptions) {
        let item = this.indoorOptions[key];
        if (item.value == 0) {
          item.highlight = true;
          res = false;
        }
      }
      return res;
    },

    checkOudoorValidation() {
      let res = true;
      for (var key1 in this.outdoorOptions) {
        let item = this.outdoorOptions[key1];
        if (item.value == 0) {
          item.highlight = true;
          res = false;
        }
      }
      return res;
    },

    checkIndoorOudoorValidation() {
      let res = true;
      let index = -1;

      for (let key in this.outdoorOptions) {
        let item = this.outdoorOptions[key];
        if (item.value == 0) {
          item.highlight = true;
          res = false;
          if (index == -1) {
            index = "outdoor-" + key;
          }
        }
      }

      for (let key in this.indoorOptions) {
        let item = this.indoorOptions[key];
        if (item.value == 0) {
          item.highlight = true;
          res = false;
          if (index == -1) {
            index = "indoor-" + key;
          }
        }
      }

      if (!res) {
        window.scrollTo({
          top: document.getElementById("" + index).offsetTop - 180,
          left: 0,
          behavior: "smooth",
        });
      }

      return res;
    },

    getSelectedIndoorOptions() {
      return this.indoorOptions.filter(function (item) {
        return item.value != 1 && item.value != 0;
      });
    },

    getSelectedOutdoorOptions() {
      return this.outdoorOptions.filter(function (item) {
        return item.value != 1 && item.value != 0;
      });
    },

    backButtonClicked() {
      this.$router.go(-1);
    },
    onScroll(e) {
      console.log("bottom!", e);
    },
    measureHeader() {
      this.headerHeight = appHeaderBottom(this.headerHeight);
    },
    handleScroll() {
      this.measureHeader();
      const sections = document.querySelectorAll(".section");
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (
          rect.top <= window.innerHeight / 2 &&
          rect.bottom >= window.innerHeight / 2
        ) {
          this.selectedTab = section.id.replace("Sec", "");
        }
      });
    },
  },
};
</script>

<style>
.heading a img {
  width: 40px;
}

.highlight {
  border: 2px solid red;
  padding: 10px;
}

.minHeight {
  min-height: 0px;
}

/* .outdoorEquipPg {
  z-index: -1;
  position: relative;
} */
</style>
